import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import Swal from 'sweetalert2';
import { convertDomainArrayToMap, getDomainData, getDomainValueForCode } from '../../library/Core/SmartFunctions';
import useInternalAdminReports from '../admin/reports/useInternalAdminReports';
import { getStudentImage } from '../../common/styles/Images';
import InitialsAvatar, { hasProfilePicture } from '../../library/SimpleControls/InitialsAvatar';
import * as storyApi from './StoryService';

const RATING_NOTES = [
    '5 — Flagship / exceptional story',
    '4 — Strong impact',
    '3 — Solid narrative',
    '2 — Emerging mention',
    '1 — Basic reference',
];

const EMPTY_FILTERS = {
    q: '',
    instituteId: '',
    programIds: [] as string[],
    passoutYears: [] as string[],
    protagonistName: '',
    narratorName: '',
    reporterName: '',
    categoryCode: '',
    rating: '',
    mappedAlumni: '',
    quoted: '',
    shared: '',
    publiclyAvailable: '',
    durationPreset: '',
    durationMinMinutes: '',
    durationMaxMinutes: '',
    datePreset: '',
    dateFrom: '',
    dateTo: '',
};

type Filters = typeof EMPTY_FILTERS;

type SelectOption = { value: string; label: string };

const YES_NO_OPTIONS: SelectOption[] = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];
const QUOTED_OPTIONS: SelectOption[] = [
    { value: 'once', label: 'Once' },
    { value: 'twice', label: 'Twice' },
    { value: '3-5', label: '3–5 times' },
    { value: 'more than 5', label: 'More than 5' },
];
const PUBLIC_OPTIONS: SelectOption[] = [
    { value: 'yes', label: 'Publicly available' },
    { value: 'no', label: 'Private' },
];
const DURATION_OPTIONS: SelectOption[] = [
    { value: 'under 1 minute', label: 'Under 1 minute' },
    { value: '1-5', label: '1–5 minutes' },
    { value: '5-15', label: '5–15 minutes' },
    { value: '15-30', label: '15–30 minutes' },
    { value: '30-60', label: '30–60 minutes' },
    { value: 'over 60', label: 'Over 60 minutes' },
    { value: 'custom', label: 'Custom minutes' },
];
const DATE_OPTIONS: SelectOption[] = [
    { value: 'last 30 days', label: 'Last 30 days' },
    { value: 'last 3 months', label: 'Last 3 months' },
    { value: 'last 6 months', label: 'Last 6 months' },
    { value: 'this year', label: 'This year' },
    { value: 'all time', label: 'All time' },
    { value: 'custom', label: 'Custom range' },
];

const SORT_OPTIONS: SelectOption[] = [
    { value: 'newest', label: 'Newest first' },
    { value: 'rating_desc', label: 'Rating: high to low' },
    { value: 'rating_asc', label: 'Rating: low to high' },
];

function optionLabel(options: SelectOption[], value: string) {
    return options.find((option) => option.value === value)?.label || value;
}

function displayDate(value: string) {
    return value ? value.split('-').reverse().join('-') : '';
}

function talkTime(seconds: number) {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const rest = total % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${rest}s`;
    if (minutes > 0) return `${minutes}m ${rest}s`;
    return `${rest}s`;
}


function requestBody(filters: Filters, page: number) {
    const body: Record<string, unknown> = { page };
    const text: Array<keyof Filters> = ['q', 'protagonistName', 'narratorName', 'reporterName', 'categoryCode', 'mappedAlumni', 'quoted', 'shared', 'publiclyAvailable', 'durationPreset', 'datePreset'];
    text.forEach((key) => {
        const value = String(filters[key] || '').trim();
        if (value) body[key] = value;
    });
    if (String(filters.instituteId).trim()) body.instituteId = Number(filters.instituteId);
    if (filters.programIds.length) body.programIds = filters.programIds.map(Number);
    if (filters.passoutYears.length) body.passoutYears = filters.passoutYears.map(Number);
    if (String(filters.rating).trim()) body.rating = Number(filters.rating);
    if (filters.durationPreset === 'custom') {
        if (String(filters.durationMinMinutes).trim()) body.durationMinMinutes = Number(filters.durationMinMinutes);
        if (String(filters.durationMaxMinutes).trim()) body.durationMaxMinutes = Number(filters.durationMaxMinutes);
    }
    if (filters.datePreset === 'custom') {
        if (filters.dateFrom) body.dateFrom = filters.dateFrom;
        if (filters.dateTo) body.dateTo = filters.dateTo;
    }
    return body;
}

type Option = { code: string; value: string };

// Same markup and behaviour as MultiSelectCustomControl2, driven by local state instead of SmartContext.
const FilterMultiSelect = ({ label, options, selected, onChange }: { label: string; options: Option[]; selected: string[]; onChange: (values: string[]) => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef: RefObject<HTMLDivElement> = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filtered = searchTerm ? options.filter((option) => option.value.toLowerCase().includes(searchTerm.toLowerCase())) : options;
    const selectedNames = options.filter((option) => selected.includes(option.code)).map((option) => option.value);
    const buttonText = selectedNames.length === 0 ? label : selectedNames.join(', ').length > 20 ? `${selectedNames.length} selected` : selectedNames.join(', ');

    const handleChange = (code: string, checked: boolean) => {
        onChange(checked ? [...selected, code] : selected.filter((item) => item !== code));
    };

    return (
        <div className="dropdown" ref={dropdownRef}>
            <button className="form-select" type="button" onClick={() => setIsOpen(!isOpen)} style={{ textAlign: 'left' }}>
                {buttonText}
            </button>
            {isOpen && (
                <div className="dropdown-menu w-100" style={{ display: 'block', background: 'white' }}>
                    <div className="dropdown-wrap">
                        <div className="p-2">
                            <input type="text" className="form-control" placeholder={`Search ${label}`} value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
                        </div>
                        <a role="button" className="dropdown-item" onClick={() => onChange([])}>
                            Clear All
                        </a>
                        {filtered.length > 0 ? (
                            filtered.map((option) => (
                                <div key={option.code} className="dropdown-item">
                                    <label>
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            value={option.code}
                                            checked={selected.includes(option.code)}
                                            onChange={(event) => handleChange(option.code, event.target.checked)}
                                        />
                                        {option.value}
                                    </label>
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-item text-muted">No results</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const StoryRepository = () => {
    const { getFormattedDate } = useInternalAdminReports();
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
    const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
    const [page, setPage] = useState(1);
    const [result, setResult] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [domain, setDomain] = useState<Map<string, any[]> | null>(null);
    const [ratingOpen, setRatingOpen] = useState(false);
    const [openGear, setOpenGear] = useState<number | string | null>(null);
    const [categories, setCategories] = useState<any[]>([]);
    // Sort is not a filter: it changes the list immediately and survives Clear All Filters.
    const [sort, setSort] = useState('newest');
    const sortRef = useRef('newest');

    async function load(nextFilters: Filters, nextPage: number) {
        setLoading(true);
        setError('');
        setResult(null);
        setOpenGear(null);
        try {
            const response = await storyApi.search({ ...requestBody(nextFilters, nextPage), sort: sortRef.current });
            setResult(response.data);
            if (Array.isArray(response.data?.categories) && response.data.categories.length) setCategories(response.data.categories);
            setApplied(nextFilters);
            setPage(nextPage);
        } catch (err: any) {
            setResult(null);
            setError(err?.response?.data?.message || 'The story list could not be loaded.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load(EMPTY_FILTERS, 1);
        getDomainData().then((rows) => {
            if (Array.isArray(rows)) setDomain(convertDomainArrayToMap(rows));
        }).catch(() => undefined);
    }, []);

    function setField(key: keyof Filters, value: string | boolean | string[]) {
        setFilters((current) => ({ ...current, [key]: value }));
    }

    // Same year list as the All Alumni "Program Completion Year" filter.
    const yearOptions: Option[] = useMemo(
        () => ((domain?.get('PAST_YEAR_CODE') || []) as any[]).map((item) => ({ code: String(item.code), value: item.value })),
        [domain]
    );

    const instituteOptions = useMemo(
        () => ((domain?.get('INSTITUTE_DOMAIN') || []) as any[]).map((item) => ({ value: String(item.code), label: item.value })),
        [domain]
    );
    const programOptions: Option[] = useMemo(() => {
        if (!filters.instituteId) return [];
        return ((domain?.get('ALL_PROGRAM_CODE') || []) as any[])
            .filter((item) => String(item.parentCode) === String(filters.instituteId))
            .map((item) => ({ code: String(item.code), value: item.value }));
    }, [domain, filters.instituteId]);
    const selectedInstitute = instituteOptions.find((option) => option.value === String(filters.instituteId)) || null;

    const loadInstitutes = (inputValue: string) => {
        if (inputValue.length < 3) return Promise.resolve([]);
        return Promise.resolve(instituteOptions.filter((option) => option.label.toUpperCase().includes(inputValue.toUpperCase())));
    };

    function setInstitute(value: string) {
        setFilters((current) => ({ ...current, instituteId: value, programIds: [] }));
    }

    const domainState = { domain } as any;
    // Alphabetical, with "Others" always last.
    const categoryOptions = [...(categories.length ? categories : (domain?.get('STORY_CATEGORY') || []))].sort((a: any, b: any) => (a.code === 'OTHERS' ? 1 : 0) - (b.code === 'OTHERS' ? 1 : 0) || String(a.value || '').localeCompare(String(b.value || ''), undefined, { sensitivity: 'base' }));

    // Pills for the dropdown filters behind the rows on screen (same markup as FiltersApplied); text boxes are not listed.
    type Pill = { key: string; label: string; value: string; remove: (current: Filters) => Filters };
    function appliedPills(current: Filters) {
        const pills: Pill[] = [];
        const clear = (...keys: Array<keyof Filters>) => (f: Filters) => keys.reduce((next, key) => ({ ...next, [key]: EMPTY_FILTERS[key] }), f);
        if (current.instituteId) {
            const institute = instituteOptions.find((option) => option.value === String(current.instituteId));
            pills.push({ key: 'instituteId', label: 'Institute', value: institute?.label || current.instituteId, remove: clear('instituteId', 'programIds') });
        }
        const programs = (domain?.get('ALL_PROGRAM_CODE') || []) as any[];
        current.programIds.forEach((id) => {
            const program =
                programs.find((item) => String(item.code) === id && String(item.parentCode) === String(current.instituteId)) ||
                programs.find((item) => String(item.code) === id);
            pills.push({ key: `programIds-${id}`, label: 'Program', value: program?.value || id, remove: (f) => ({ ...f, programIds: f.programIds.filter((item) => item !== id) }) });
        });
        current.passoutYears.forEach((year) => {
            pills.push({ key: `passoutYears-${year}`, label: 'Passout year', value: year, remove: (f) => ({ ...f, passoutYears: f.passoutYears.filter((item) => item !== year) }) });
        });
        if (current.categoryCode) {
            const category = categoryOptions.find((item: any) => item.code === current.categoryCode);
            pills.push({ key: 'categoryCode', label: 'Category', value: category?.value || current.categoryCode, remove: clear('categoryCode') });
        }
        if (current.rating) pills.push({ key: 'rating', label: 'Rating', value: String(current.rating), remove: clear('rating') });
        if (current.mappedAlumni) pills.push({ key: 'mappedAlumni', label: 'Mapped alumni', value: optionLabel(YES_NO_OPTIONS, current.mappedAlumni), remove: clear('mappedAlumni') });
        if (current.quoted) pills.push({ key: 'quoted', label: 'Quoted', value: optionLabel(QUOTED_OPTIONS, current.quoted), remove: clear('quoted') });
        if (current.shared) pills.push({ key: 'shared', label: 'Shared', value: optionLabel(YES_NO_OPTIONS, current.shared), remove: clear('shared') });
        if (current.publiclyAvailable) pills.push({ key: 'publiclyAvailable', label: 'LinkedIn or Google', value: optionLabel(PUBLIC_OPTIONS, current.publiclyAvailable), remove: clear('publiclyAvailable') });
        if (current.durationPreset) {
            const min = String(current.durationMinMinutes).trim();
            const max = String(current.durationMaxMinutes).trim();
            let text = optionLabel(DURATION_OPTIONS, current.durationPreset);
            if (current.durationPreset === 'custom') {
                if (min && max) text = `${min}–${max} minutes`;
                else if (min) text = `from ${min} minutes`;
                else if (max) text = `up to ${max} minutes`;
            }
            pills.push({ key: 'durationPreset', label: 'Call duration', value: text, remove: clear('durationPreset', 'durationMinMinutes', 'durationMaxMinutes') });
        }
        if (current.datePreset) {
            let text: string | null = optionLabel(DATE_OPTIONS, current.datePreset);
            if (current.datePreset === 'custom') {
                const from = displayDate(current.dateFrom);
                const to = displayDate(current.dateTo);
                text = from && to ? `${from} to ${to}` : from ? `from ${from}` : to ? `up to ${to}` : null;
            }
            if (text) pills.push({ key: 'datePreset', label: 'Call date', value: text, remove: clear('datePreset', 'dateFrom', 'dateTo') });
        }
        return pills;
    }
    const pills = appliedPills(applied);

    function removePill(pill: Pill) {
        setFilters((current) => pill.remove(current));
        load(pill.remove(applied), 1);
    }
    const rows = result?.rows || [];
    const total = Number(result?.total) || 0;
    const pageSize = Number(result?.pageSize) || 25;
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    const counts = result?.counts || { logEntries: 0, outreachCalls: 0, alumniNames: 0, talkTimeSeconds: 0 };

    function label(category: string, code: string | null | undefined) {
        if (!code) return '';
        return getDomainValueForCode(code, category, domainState) || '';
    }

    async function removeStory(row: any) {
        setOpenGear(null);
        const confirm = await Swal.fire({
            title: 'Delete this story?',
            text: 'The story will be removed from the repository. Profile markers stay as they are.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Dismiss',
        });
        if (!confirm.isConfirmed) return;
        try {
            await storyApi.remove(row.id);
            await load(applied, page);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'The story could not be deleted.');
        }
    }

    return (
        <div className="bg-white">
            <div className="max-1140 mx-auto py-5">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h1 className="h3 mb-0">Story Repository</h1>
                    <button type="button" className="btn btn-green btn-link" onClick={() => setRatingOpen((open) => !open)}><span>How rating works</span></button>
                </div>
                {ratingOpen && (
                    <div className="alert alert-success border border-success-subtle">
                        {RATING_NOTES.map((note) => <div key={note}>{note}</div>)}
                    </div>
                )}

                <div className='border rounded bg-light p-4 mb-4 shadow-sm'>
                    <form className="row g-3" onSubmit={(event) => { event.preventDefault(); load(filters, 1); }}>
                    <div className="col-md-12">
                        <label className="form-label small fw-medium text-dark mb-1">Search across every field</label>
                        <input className="form-control form-search" placeholder="Search any word — protagonist, story, category, notes… e.g. &quot;CEO&quot;, &quot;married&quot;, &quot;startup in Dubai&quot;" value={filters.q} onChange={(event) => setField('q', event.target.value)} />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label small fw-medium text-dark mb-1">Institute Name</label>
                        <div className='lstCustForm' >
                            <AsyncSelect
                            cacheOptions
                            loadOptions={loadInstitutes}
                            
                            value={selectedInstitute}
                            onChange={(option: any) => setInstitute(option ? option.value : '')}
                            placeholder="Institute"
                            noOptionsMessage={({ inputValue }) => (inputValue.length < 3 ? 'Type at least 3 characters' : 'No results')}
                            isClearable
                        />
                        </div>
                        
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-medium text-dark mb-1">Program</label>
                        <FilterMultiSelect label="Program" options={programOptions} selected={filters.programIds} onChange={(values) => setField('programIds', values)} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-medium text-dark mb-1">Passout year</label>
                        <FilterMultiSelect label="Passout year" options={yearOptions} selected={filters.passoutYears} onChange={(values) => setField('passoutYears', values)} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-medium text-dark mb-1">Protagonist Alumni Name</label>
                        <input className="form-control" placeholder="Protagonist name" value={filters.protagonistName} onChange={(event) => setField('protagonistName', event.target.value)} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-medium text-dark mb-1">Narrator</label>
                        <input className="form-control" placeholder="Narrator" value={filters.narratorName} onChange={(event) => setField('narratorName', event.target.value)} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-medium text-dark mb-1">Reporter</label>
                        <input className="form-control" placeholder="Reporter" value={filters.reporterName} onChange={(event) => setField('reporterName', event.target.value)} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-medium text-dark mb-1">Category</label>
                        <select className="form-select" value={filters.categoryCode} onChange={(event) => setField('categoryCode', event.target.value)}>
                            <option value="">Any category</option>
                            {categoryOptions.map((item: any) => <option key={item.code} value={item.code}>{item.value}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-medium text-dark mb-1">Story Rating</label>
                        <select className="form-select" value={filters.rating} onChange={(event) => setField('rating', event.target.value)}>
                            <option value="">Any rating</option>
                            {[1, 2, 3, 4, 5].map((value) => <option key={value} value={value}>{value}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-medium text-dark mb-1">Mapped Alumni</label>
                        <select className="form-select" value={filters.mappedAlumni} onChange={(event) => setField('mappedAlumni', event.target.value)}>
                            <option value="">Mapped alumni</option>
                            {YES_NO_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-medium text-dark mb-1">Quoted Multiple Times</label>
                        <select className="form-select" value={filters.quoted} onChange={(event) => setField('quoted', event.target.value)}>
                            <option value="">Quoted</option>
                            {QUOTED_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-medium text-dark mb-1">Shared with Management</label>
                        <select className="form-select" value={filters.shared} onChange={(event) => setField('shared', event.target.value)}>
                            <option value="">Shared</option>
                            {YES_NO_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label small fw-medium text-dark mb-1">Is information on LinkedIn or first 3 Google pages</label>
                        <select className="form-select" value={filters.publiclyAvailable} onChange={(event) => setField('publiclyAvailable', event.target.value)}>
                            <option value="">LinkedIn or Google</option>
                            {PUBLIC_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small fw-medium text-dark mb-1">Call duration</label>
                        <select className="form-select" value={filters.durationPreset} onChange={(event) => setField('durationPreset', event.target.value)}>
                            <option value="">Call duration</option>
                            {DURATION_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    </div>
                    {filters.durationPreset === 'custom' && (
                        <>
                            <div className="col-md-2">
                                <label className="form-label small fw-medium text-dark mb-1">To</label>
                                <input className="form-control" placeholder="Min minutes" value={filters.durationMinMinutes} onChange={(event) => setField('durationMinMinutes', event.target.value)} /></div>
                            <div className="col-md-2">
                                <label className="form-label small fw-medium text-dark mb-1">From</label>
                                <input className="form-control" placeholder="Max minutes" value={filters.durationMaxMinutes} onChange={(event) => setField('durationMaxMinutes', event.target.value)} /></div>
                        </>
                    )}
                    <div className="col-md-2">
                        <label className="form-label small fw-medium text-dark mb-1">Call date</label>
                        <select className="form-select" value={filters.datePreset} onChange={(event) => setField('datePreset', event.target.value)}>
                            <option value="">Call date</option>
                            {DATE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    </div>
                    {filters.datePreset === 'custom' && (
                        <>
                            <div className="col-md-2">
                                <label className="form-label small fw-medium text-dark mb-1">To</label>
                                <input className="form-control" type="date" value={filters.dateFrom} onChange={(event) => setField('dateFrom', event.target.value)} /></div>
                            <div className="col-md-2">
                                <label className="form-label small fw-medium text-dark mb-1">From</label>
                                <input className="form-control" type="date" value={filters.dateTo} onChange={(event) => setField('dateTo', event.target.value)} /></div>
                        </>
                    )}
                    <div className="col-md-2">
                        <label className="form-label small fw-medium text-dark mb-1">Sort by</label>
                        <select
                            className="form-select"
                            value={sort}
                            onChange={(event) => {
                                sortRef.current = event.target.value;
                                setSort(event.target.value);
                                load(applied, 1);
                            }}>
                            {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    </div>
                    <div className="col-12 d-flex flex-wrap align-items-center gap-2">
                        <button type="submit" className="btn btn-primary">Apply</button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => { setFilters(EMPTY_FILTERS); load(EMPTY_FILTERS, 1); }}>Clear All Filters</button>
                        {pills.length > 0 && (
                            <div className="d-flex flex-wrap align-items-center">
                                {pills.map((pill) => (
                                    <div key={pill.key} className="badge rounded-pill text-bg-primary p-2 m-1">
                                        {pill.label} : {pill.value}
                                        <i className="bi bi-x-circle p-2" role="button" aria-label={`Remove ${pill.label} ${pill.value}`} onClick={() => removePill(pill)}></i>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </form>
                </div>
                

                {error && <div className="alert alert-warning">{error}</div>}

                <div className="d-flex flex-wrap gap-3 mb-2">
                    <span>Log entries: {loading ? '—' : counts.logEntries}</span>
                    <span>Outreach calls: {loading ? '—' : counts.outreachCalls}</span>
                    <span>Alumni names: {loading ? '—' : counts.alumniNames}</span>
                    <span>Talk time: {loading ? '—' : talkTime(counts.talkTimeSeconds)}</span>
                </div>

                {loading && <div>Loading stories…</div>}
                {!loading && !error && total === 0 && (
                    <div className="alert alert-light border">
                        <div>No stories match these filters</div>
                        <div>Widen the date range or clear the search.</div>
                    </div>
                )}
                {!loading && !error && total > 0 && (
                    <>
                    <div className='card border rounded-3 bg-white overflow-hidden mb-3 shadow-sm'>

                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 small">
                                <thead>
                                    <tr>
                                        <th className='ps-3 py-3 fw-semibold text-dark' style={{width:'200px'}}>Alumni Name</th>
                                        <th className='py-3 fw-semibold text-dark'>Institute</th>
                                        <th className='py-3 fw-semibold text-dark' style={{width:'400px'}}>Category & Outreach Note</th>
                                        <th className='py-3 fw-semibold text-dark'>Present Status</th>
                                        <th className='py-3 fw-semibold text-dark'>Created on</th>
                                        <th className='pe-3 py-3 fw-semibold text-dark'>Updated on</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row: any) => {
                                        const education = row.presentEducationStatus || label('PRESENT_EDUCATION_STATUS_CODE', row.presentEducationStatusCode);
                                        const working = row.workingStatus || label('WORK_STATUS_CODE', row.workingStatusCode);
                                        const completion = row.profileCompletionPercentage === null || row.profileCompletionPercentage === undefined || row.profileCompletionPercentage === ''
                                            ? ''
                                            : `Profile Completion - ${Number(row.profileCompletionPercentage) < 40 ? 'Less than 40%' : `${row.profileCompletionPercentage}%`}`;
                                        return (
                                            <tr key={row.id}>
                                                <td className='ps-3 py-3'>
                                                    <a className="tablink d-flex align-items-start" href={`/alumni/${row.protagonistUserId}/profile`} target="_blank" rel="noopener noreferrer">
                                                        <i className="me-2 mt-1 position-relative flex-shrink-0">
                                                            {hasProfilePicture(row.profilePictureFilePath) ? (
                                                                <img src={getStudentImage(row)} className="rounded-circle img-fluid" width="30" />
                                                            ) : (
                                                                <InitialsAvatar firstName={row.firstName} lastName={row.lastName} name={row.protagonistName} size={30} />
                                                            )}
                                                        </i>
                                                        <span className="d-flex flex-column">
                                                            <span>{row.protagonistName}</span>
                                                            {(row.programName || row.passoutYear) && (
                                                                <span className="small text-muted">{[row.programName, row.passoutYear].filter(Boolean).join(' · ')}</span>
                                                            )}
                                                        </span>
                                                    </a>
                                                </td>
                                                <td className='py-3 small fw-medium text-black'>{row.instituteName}</td>
                                                <td className='py-3'>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {(row.categories || [])
                                                            .map((item: any) => item.label || item.code)
                                                            .filter(Boolean)
                                                            .map((label: string, index: number) => (
                                                            <span
                                                                key={`${label}-${index}`}
                                                                className="badge bg-primary bg-opacity-10 text-dark mb-1 font-12 font-500 d-inline-block rounded-2 py-1 px-2"
                                                            >
                                                                {label}
                                                            </span>
                                                            ))}
                                                        </div>
                                                    <div>{row.whyNoteworthy}</div>
                                                </td>
                                                <td className='py-3'>
                                                    <div>{education}</div>
                                                    <div>{working}</div>
                                                    <div>{completion}</div>
                                                </td>
                                                <td className='py-3 small text-muted'>{getFormattedDate(row.createdAt)}</td>
                                                <td className='pe-3 py-3 small text-muted'>{getFormattedDate(row.updatedAt)}</td>
                                                <td>
                                                    <div className="d-inline-block position-relative mailSetting me-2">
                                                        <i role="button" className="icon-setting text-gray-84 pe-0" aria-label="Story actions" onClick={() => setOpenGear(openGear === row.id ? null : row.id)}></i>
                                                        {openGear === row.id && (
                                                            <div className="setting-dropdown open">
                                                                <Link className="dropdown-item" to={`/support/story/${row.id}`}>Preview</Link>
                                                                <Link className="dropdown-item" to={`/support/story/${row.id}/edit`}>Edit</Link>
                                                                <button type="button" className="dropdown-item" onClick={() => removeStory(row)}>Delete</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                        <div className="d-flex justify-content-between align-items-center">
                            <div>Showing {from} to {to} of {total}</div>
                            <div className="d-flex gap-2">
                                <button type="button" className="btn btn-outline-secondary btn-sm rounded px-3 py-1 small cursor-pointer" disabled={page <= 1} onClick={() => load(applied, page - 1)}>Previous</button>
                                <button type="button" className="btn btn-outline-secondary btn-sm rounded px-3 py-1 small cursor-pointer" disabled={to >= total} onClick={() => load(applied, page + 1)}>Next</button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default StoryRepository;
